/**
 * Modèle pur de l'éditeur de schéma (F2-Forms-1c-ii).
 *
 * Logique sans React : types d'édition, génération/déduplication de clés stables,
 * opérations immuables (add/remove/move/update sections·champs·options,
 * changement de type), collecte des clés « persistées » (verrouillées), validation
 * brouillon via le contrat PERMISSIF shared. Testable isolément (schemaModel.test).
 *
 * Règle clés (décision PO §2) : une clé est éditable UNIQUEMENT tant que l'élément
 * n'est pas présent dans la baseline serveur (= dernier schéma sauvegardé). Après
 * Save, la baseline est remplacée → toutes ses clés deviennent verrouillées. Les
 * clés héritées d'une version copiée sont donc verrouillées d'emblée. Changer un
 * libellé ne change JAMAIS une clé.
 */
import { Schemas } from "@kollectivia/shared";

export const FIELD_TYPES = Schemas.FORM_FIELD_TYPES;
export type FieldType = (typeof FIELD_TYPES)[number];

// 1e-B — rôle de mapping (optionnel). Réexporte la source de vérité shared.
export const FIELD_ROLES = Schemas.FORM_FIELD_ROLES;
export type FieldRole = Schemas.FormFieldRole;

/** Rôles compatibles avec un type de champ donné (filtrage du `<select>` éditeur). */
/** 9.BJ.3 — les sources d'amorçage compatibles avec un type de champ. */
export const PREFILL_SOURCES = Schemas.FORM_PREFILL_SOURCES;
export type PrefillSource = Schemas.FormPrefillSource;

export function prefillSourcesForType(type: FieldType): readonly PrefillSource[] {
  return PREFILL_SOURCES.filter((src) => Schemas.prefillAllowsType(src, type));
}

export function rolesForType(type: FieldType): readonly FieldRole[] {
  return FIELD_ROLES.filter((r) => Schemas.roleAllowsType(r, type));
}

export type Bilingual = { fr: string; en: string };
// `_uid` : identité client-only stable (clés React → focus conservé au reorder).
// JAMAIS envoyé au serveur (retiré par serializeSchema).
export type EditorOption = { _uid: string; value: string; label: Bilingual };
export type EditorValidation = {
  min_length?: number;
  max_length?: number;
  regex?: string;
  min?: number;
  exclusive_min?: number;
  max?: number;
  integer?: boolean;
};
export type EditorField = {
  _uid: string;
  key: string;
  type: FieldType;
  label: Bilingual;
  help?: Bilingual;
  placeholder?: Bilingual;
  required: boolean;
  active_when?: Schemas.FormFieldConditions;
  required_when?: Schemas.FormFieldConditions;
  options?: EditorOption[];
  validation?: EditorValidation;
  role?: FieldRole;
  /** 9.BJ.3 — d'où le serveur amorce la valeur. Sens INVERSE de `role`. */
  prefill_from?: PrefillSource;
  pdf_format?: "decimal" | "cad" | "percent";
};
/**
 * Un bloc de contenu — chunk 9.BJ.2.
 *
 * Ni type, ni `required`, ni `role`, ni options : il n'y a rien à saisir. Sa
 * seule donnée est un texte bilingue, et sa clé sert à l'unicité et à React.
 */
export type EditorBlock = { _uid: string; key: string; kind: "paragraph"; text: Bilingual };
export type EditorSection = {
  amount_relations?: Schemas.FormVersionSchemaType["sections"][number]["amount_relations"];
  coverage_choice_key?: string;
  _uid: string;
  key: string;
  label: Bilingual;
  fields: EditorField[];
  blocks: EditorBlock[];
};

let _uidCounter = 0;
function uid(): string {
  _uidCounter += 1;
  return `u${_uidCounter}`;
}
export type EditorSchema = {
  sections: EditorSection[];
  version_metadata: {
    schema_version: 1;
    purpose?: "mandate" | "business_spec" | "other";
    content_languages?: ("fr" | "en")[];
    document_profile?: Schemas.DocumentProfile;
  };
};

export const STABLE_KEY_RE = /^[a-z][a-z0-9_]{0,62}$/;
export const TYPES_WITH_OPTIONS: ReadonlySet<FieldType> = new Set(["select", "multi_select"]);
export const TYPES_WITH_VALIDATION: ReadonlySet<FieldType> = new Set(["text", "textarea"]);

export function typeNeedsOptions(t: FieldType): boolean {
  return TYPES_WITH_OPTIONS.has(t);
}
export function typeSupportsValidation(t: FieldType): boolean {
  return TYPES_WITH_VALIDATION.has(t);
}

/** Slug déterministe → format clé stable (sans accents), vide si rien d'exploitable. */
export function slugifyKey(input: string): string {
  const base = input
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_{2,}/g, "_")
    .replace(/^[^a-z]+/, "");
  return base.slice(0, 63);
}

/** Rend `candidate` unique vis-à-vis de `taken` (suffixe _2, _3, … déterministe). */
export function ensureUniqueKey(candidate: string, taken: ReadonlySet<string>): string {
  if (candidate && !taken.has(candidate)) return candidate;
  let n = 2;
  const stem = candidate || "x";
  let next = `${stem}_${n}`.slice(0, 63);
  while (taken.has(next)) {
    n += 1;
    next = `${stem}_${n}`.slice(0, 63);
  }
  return next;
}

/**
 * Clé auto : libellé langue par défaut → autre langue → `${prefix}_${index}`.
 * Ne priorise jamais systématiquement l'anglais (décision PO §2).
 */
export function autoKey(
  label: Bilingual,
  defaultLang: "fr" | "en",
  prefix: string,
  index1: number,
  taken: ReadonlySet<string>,
): string {
  const other = defaultLang === "fr" ? "en" : "fr";
  const fromDefault = slugifyKey(label[defaultLang]);
  const fromOther = slugifyKey(label[other]);
  const seed = fromDefault || fromOther || `${prefix}_${index1}`;
  return ensureUniqueKey(seed, taken);
}

/**
 * Tous les `_uid` d'un schéma. Sert à figer les clés des éléments PERSISTÉS
 * (présents dans la baseline serveur) : le verrouillage suit l'identité de
 * l'élément, jamais la valeur courante de sa clé (sinon taper une clé qui
 * collisionne avec une clé existante verrouillerait à tort le nouvel élément).
 */
export function collectUids(schema: EditorSchema): Set<string> {
  const out = new Set<string>();
  for (const s of schema.sections) {
    out.add(s._uid);
    for (const f of s.fields) {
      out.add(f._uid);
      for (const o of f.options ?? []) out.add(o._uid);
    }
    for (const b of s.blocks) out.add(b._uid);
  }
  return out;
}

/**
 * Toutes les clés d'un schéma (sections + champs + blocs + valeurs d'options).
 *
 * 9.BJ.2 — les blocs entrent dans le MÊME ensemble que les champs : le contrat
 * partagé refuse un doublon entre les deux, et une génération de clé qui les
 * ignorerait produirait un brouillon non publiable sans le dire.
 */
export function collectKeys(schema: EditorSchema): Set<string> {
  const out = new Set<string>();
  for (const s of schema.sections) {
    out.add(s.key);
    for (const f of s.fields) {
      out.add(f.key);
      for (const o of f.options ?? []) out.add(o.value);
    }
    for (const b of s.blocks) out.add(b.key);
  }
  return out;
}

export const EMPTY_SCHEMA: EditorSchema = { sections: [], version_metadata: { schema_version: 1 } };

/** Normalise un schéma serveur (unknown) en état d'édition sûr (+ `_uid` client). */
export function toEditorSchema(raw: unknown): EditorSchema {
  const r = raw as { sections?: unknown } | null | undefined;
  const rawSections = Array.isArray(r?.sections) ? (r!.sections as unknown[]) : [];
  const sections: EditorSection[] = rawSections.map((sRaw) => {
    const s = sRaw as Partial<EditorSection> & { fields?: unknown[] };
    const fields: EditorField[] = (Array.isArray(s.fields) ? s.fields : []).map((fRaw) => {
      const f = fRaw as Partial<EditorField> & { options?: unknown[] };
      const field: EditorField = {
        _uid: uid(),
        key: f.key ?? "",
        type: (f.type as FieldType) ?? "text",
        label: f.label ?? { fr: "", en: "" },
        required: Boolean(f.required),
      };
      if (f.help) field.help = f.help;
      if (f.placeholder) field.placeholder = f.placeholder;
      if (f.validation) field.validation = f.validation;
      if (f.active_when) field.active_when = f.active_when;
      if (f.required_when) field.required_when = f.required_when;
      if (f.role) field.role = f.role;
      if (f.prefill_from) field.prefill_from = f.prefill_from;
      if (f.pdf_format) field.pdf_format = f.pdf_format;
      if (Array.isArray(f.options)) {
        field.options = f.options.map((oRaw) => {
          const o = oRaw as Partial<EditorOption>;
          return { _uid: uid(), value: o.value ?? "", label: o.label ?? { fr: "", en: "" } };
        });
      }
      return field;
    });
    const blocks: EditorBlock[] = (
      Array.isArray((s as { blocks?: unknown[] }).blocks)
        ? ((s as { blocks: unknown[] }).blocks ?? [])
        : []
    ).map((bRaw) => {
      const b = bRaw as Partial<EditorBlock>;
      return {
        _uid: uid(),
        key: b.key ?? "",
        kind: "paragraph" as const,
        text: b.text ?? { fr: "", en: "" },
      };
    });
    return {
      _uid: uid(),
      key: s.key ?? "",
      ...(s.coverage_choice_key ? { coverage_choice_key: s.coverage_choice_key } : {}),
      ...(s.amount_relations ? { amount_relations: s.amount_relations } : {}),
      label: s.label ?? { fr: "", en: "" },
      fields,
      blocks,
    };
  });
  const purpose = Schemas.formPurpose(raw);
  const languages = (raw as EditorSchema | null)?.version_metadata?.content_languages;
  const profile = (raw as EditorSchema | null)?.version_metadata?.document_profile;
  return {
    sections,
    version_metadata: {
      schema_version: 1,
      ...(purpose === "unclassified" ? {} : { purpose }),
      ...(languages ? { content_languages: languages } : {}),
      ...(profile ? { document_profile: profile } : {}),
    },
  };
}

function newField(
  defaultLang: "fr" | "en",
  taken: ReadonlySet<string>,
  index1: number,
): EditorField {
  const label: Bilingual = { fr: "", en: "" };
  // Génération via autoKey (§2) : label vide à la création → fallback `field_N`.
  return {
    _uid: uid(),
    key: autoKey(label, defaultLang, "field", index1, taken),
    type: "text",
    label,
    required: false,
  };
}
function newSection(
  defaultLang: "fr" | "en",
  taken: ReadonlySet<string>,
  index1: number,
): EditorSection {
  const label: Bilingual = { fr: "", en: "" };
  return {
    _uid: uid(),
    key: autoKey(label, defaultLang, "section", index1, taken),
    label,
    fields: [],
    blocks: [],
  };
}

function newBlock(
  defaultLang: "fr" | "en",
  taken: ReadonlySet<string>,
  index1: number,
): EditorBlock {
  const label: Bilingual = { fr: "", en: "" };
  return {
    _uid: uid(),
    key: autoKey(label, defaultLang, "block", index1, taken),
    kind: "paragraph",
    text: { fr: "", en: "" },
  };
}

// ---------------------------------------------------------------------------
// Opérations immuables (clone + mutation locale). Schémas petits → clone OK.
// ---------------------------------------------------------------------------
function clone(s: EditorSchema): EditorSchema {
  return structuredClone(s);
}

export function addSection(schema: EditorSchema, defaultLang: "fr" | "en"): EditorSchema {
  const next = clone(schema);
  next.sections.push(newSection(defaultLang, collectKeys(next), next.sections.length + 1));
  return next;
}
export function removeSection(schema: EditorSchema, si: number): EditorSchema {
  const next = clone(schema);
  next.sections.splice(si, 1);
  return next;
}
export function moveSection(schema: EditorSchema, si: number, dir: -1 | 1): EditorSchema {
  const next = clone(schema);
  const j = si + dir;
  if (j < 0 || j >= next.sections.length) return schema;
  [next.sections[si], next.sections[j]] = [next.sections[j]!, next.sections[si]!];
  return next;
}
export function updateSection(
  schema: EditorSchema,
  si: number,
  patch: Partial<Pick<EditorSection, "key" | "label">>,
): EditorSchema {
  const next = clone(schema);
  const s = next.sections[si];
  if (!s) return schema;
  if (patch.key !== undefined) s.key = patch.key;
  if (patch.label !== undefined) s.label = patch.label;
  return next;
}

export function addField(schema: EditorSchema, si: number, defaultLang: "fr" | "en"): EditorSchema {
  const next = clone(schema);
  const s = next.sections[si];
  if (!s) return schema;
  s.fields.push(newField(defaultLang, collectKeys(next), s.fields.length + 1));
  return next;
}
export function removeField(schema: EditorSchema, si: number, fi: number): EditorSchema {
  const next = clone(schema);
  next.sections[si]?.fields.splice(fi, 1);
  return next;
}
export function moveField(schema: EditorSchema, si: number, fi: number, dir: -1 | 1): EditorSchema {
  const next = clone(schema);
  const fields = next.sections[si]?.fields;
  if (!fields) return schema;
  const j = fi + dir;
  if (j < 0 || j >= fields.length) return schema;
  [fields[fi], fields[j]] = [fields[j]!, fields[fi]!];
  return next;
}
export function updateField(
  schema: EditorSchema,
  si: number,
  fi: number,
  patch: Partial<EditorField>,
): EditorSchema {
  const next = clone(schema);
  const f = next.sections[si]?.fields[fi];
  if (!f) return schema;
  Object.assign(f, patch);
  return next;
}

// ---------------------------------------------------------------------------
// Blocs de contenu (9.BJ.2) — mêmes gestes que les champs, sans type ni options.
// ---------------------------------------------------------------------------
export function addBlock(schema: EditorSchema, si: number, defaultLang: "fr" | "en"): EditorSchema {
  const next = clone(schema);
  const s = next.sections[si];
  if (!s) return schema;
  s.blocks.push(newBlock(defaultLang, collectKeys(next), s.blocks.length + 1));
  return next;
}
export function removeBlock(schema: EditorSchema, si: number, bi: number): EditorSchema {
  const next = clone(schema);
  next.sections[si]?.blocks.splice(bi, 1);
  return next;
}
export function moveBlock(schema: EditorSchema, si: number, bi: number, dir: -1 | 1): EditorSchema {
  const next = clone(schema);
  const blocks = next.sections[si]?.blocks;
  if (!blocks) return schema;
  const j = bi + dir;
  if (j < 0 || j >= blocks.length) return schema;
  [blocks[bi], blocks[j]] = [blocks[j]!, blocks[bi]!];
  return next;
}
export function updateBlock(
  schema: EditorSchema,
  si: number,
  bi: number,
  patch: Partial<EditorBlock>,
): EditorSchema {
  const next = clone(schema);
  const b = next.sections[si]?.blocks[bi];
  if (!b) return schema;
  Object.assign(b, patch);
  return next;
}

/**
 * Changement de type déterministe : purge les propriétés incompatibles.
 * → si le nouveau type ne prend pas d'options : retire `options`.
 * → si le nouveau type ne supporte pas la validation : retire `validation`.
 * → si le nouveau type prend des options et n'en a pas : initialise `[]`
 *   (sauvegardable vide en brouillon ; la publication stricte tranchera en 1c-iii).
 */
export function changeFieldType(
  schema: EditorSchema,
  si: number,
  fi: number,
  type: FieldType,
): EditorSchema {
  const next = clone(schema);
  const f = next.sections[si]?.fields[fi];
  if (!f) return schema;
  f.type = type;
  if (type !== "number") delete f.pdf_format;
  if (!typeNeedsOptions(type)) delete f.options;
  else if (!f.options) f.options = [];
  if (!typeSupportsValidation(type)) delete f.validation;
  // 1e-B — un rôle devenu incompatible avec le nouveau type est retiré (parité options).
  if (f.role && !Schemas.roleAllowsType(f.role, type)) delete f.role;
  // 9.BJ.3 — même traitement pour la source d'amorçage : la garder produirait un
  // schéma non publiable, et l'éditeur ne dirait pas pourquoi.
  if (f.prefill_from && !Schemas.prefillAllowsType(f.prefill_from, type)) delete f.prefill_from;
  return next;
}

export function addOption(
  schema: EditorSchema,
  si: number,
  fi: number,
  defaultLang: "fr" | "en",
): EditorSchema {
  const next = clone(schema);
  const f = next.sections[si]?.fields[fi];
  if (!f) return schema;
  if (!f.options) f.options = [];
  const label: Bilingual = { fr: "", en: "" };
  f.options.push({
    _uid: uid(),
    value: autoKey(label, defaultLang, "option", f.options.length + 1, collectKeys(next)),
    label,
  });
  return next;
}
export function removeOption(
  schema: EditorSchema,
  si: number,
  fi: number,
  oi: number,
): EditorSchema {
  const next = clone(schema);
  next.sections[si]?.fields[fi]?.options?.splice(oi, 1);
  return next;
}
export function moveOption(
  schema: EditorSchema,
  si: number,
  fi: number,
  oi: number,
  dir: -1 | 1,
): EditorSchema {
  const next = clone(schema);
  const opts = next.sections[si]?.fields[fi]?.options;
  if (!opts) return schema;
  const j = oi + dir;
  if (j < 0 || j >= opts.length) return schema;
  [opts[oi], opts[j]] = [opts[j]!, opts[oi]!];
  return next;
}
export function updateOption(
  schema: EditorSchema,
  si: number,
  fi: number,
  oi: number,
  patch: Partial<EditorOption>,
): EditorSchema {
  const next = clone(schema);
  const o = next.sections[si]?.fields[fi]?.options?.[oi];
  if (!o) return schema;
  if (patch.value !== undefined) o.value = patch.value;
  if (patch.label !== undefined) o.label = patch.label;
  return next;
}

/**
 * Prépare le `schema` pour le PATCH : retire les blocs optionnels vides (help/
 * placeholder tout-vides, validation sans champ) pour un JSON propre. Ne juge
 * PAS de la publiabilité (contrat permissif au Save).
 */
export function serializeSchema(schema: EditorSchema): unknown {
  const out = clone(schema) as EditorSchema;
  const strip = (obj: { _uid?: string }) => {
    delete obj._uid;
  };
  for (const s of out.sections) {
    strip(s);
    for (const b of s.blocks) strip(b);
    // `blocks` absent plutôt que `[]` : le contrat le déclare optionnel, et un
    // tableau vide sur chaque section alourdirait tous les schémas existants
    // pour une information qui n'en est pas une.
    if (s.blocks.length === 0) delete (s as { blocks?: unknown }).blocks;
    for (const f of s.fields) {
      strip(f);
      if (!f.role) delete f.role;
      if (!f.prefill_from) delete f.prefill_from;
      if (f.help && !f.help.fr && !f.help.en) delete f.help;
      if (f.placeholder && !f.placeholder.fr && !f.placeholder.en) delete f.placeholder;
      if (
        f.validation &&
        f.validation.min_length === undefined &&
        f.validation.max_length === undefined &&
        !f.validation.regex &&
        f.validation.min === undefined &&
        f.validation.exclusive_min === undefined &&
        f.validation.max === undefined &&
        f.validation.integer === undefined
      ) {
        delete f.validation;
      }
      for (const o of f.options ?? []) strip(o);
    }
  }
  return out;
}

export type DraftIssue = {
  path: string;
  message: string;
  code: string;
  kind?: string;
  minimum?: number;
  maximum?: number;
};
export type DraftValidation = { ok: boolean; issues: DraftIssue[] };

/** Valide contre le contrat PERMISSIF (brouillon) — jamais le contrat strict de publication. */
export function validateDraft(schema: EditorSchema): DraftValidation {
  const parsed = Schemas.FormVersionDraftSchemaContract.safeParse(serializeSchema(schema));
  if (parsed.success) return { ok: true, issues: [] };
  return {
    ok: false,
    issues: parsed.error.issues.slice(0, 50).map((i) => ({
      path: i.path.join("."),
      message: i.message,
      code: i.code,
      kind: "type" in i ? i.type : undefined,
      minimum: "minimum" in i && typeof i.minimum === "number" ? i.minimum : undefined,
      maximum: "maximum" in i && typeof i.maximum === "number" ? i.maximum : undefined,
    })),
  };
}

/** A per-response choice, never an answer or preset on the published template. */
export function enableCoverageChoice(schema: EditorSchema, si: number): EditorSchema {
  if (
    schema.version_metadata.purpose !== "business_spec" ||
    schema.sections[si]?.coverage_choice_key
  )
    return schema;
  const next = structuredClone(schema);
  const section = next.sections[si];
  if (!section) return schema;
  const taken = collectKeys(next);
  let key = "coverage_choice";
  for (let i = 2; taken.has(key); i++) key = `coverage_choice_${i}`;
  section.coverage_choice_key = key;
  section.fields.unshift({
    _uid: uid(),
    key,
    type: "select",
    required: true,
    label: { fr: "Garantie demandée", en: "Requested coverage" },
    options: [
      { _uid: uid(), value: "included", label: { fr: "Incluse", en: "Included" } },
      { _uid: uid(), value: "optional", label: { fr: "En option", en: "Optional" } },
      { _uid: uid(), value: "not_requested", label: { fr: "Non demandée", en: "Not requested" } },
    ],
  });
  return next;
}
